import React, { useEffect, useRef, useState } from "react";
import { MoreVertical } from "lucide-react";

/**
 * The ⋮ menu of a pathway (spec screen 1: Edit / Duplicate / Archive), used on the list
 * cards and in the dashboard header.
 *
 * Plain markup (no portal), so it inherits the console's theme and needs nothing a Jest
 * suite has to mock. Escape or a press outside closes it; choosing an item closes it
 * before the item runs, so an item that opens a dialog or navigates away is not left
 * with a menu hanging over it.
 *
 * @param {object} props
 * @param {string} props.label       accessible name of the ⋮ button
 * @param {string} props.testId      the ⋮ button's test id
 * @param {{ key: string, label: string, onSelect: () => void, disabled?: boolean,
 *           danger?: boolean, testId: string }[]} props.items
 * @param {string} [props.buttonClassName]
 */
export default function ActionMenu({ label, testId, items, buttonClassName = "" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onPointer = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        data-testid={testId}
        className={`p-1.5 rounded-lg text-eh-muted hover:text-eh-ink hover:bg-eh-cream ${buttonClassName}`}
      >
        <MoreVertical className="w-4 h-4" aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" aria-label={label} className="absolute right-0 mt-1 w-44 z-20 rounded-xl border border-eh-border-soft bg-white shadow-lg p-1.5">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              data-testid={item.testId}
              onClick={() => { setOpen(false); item.onSelect(); }}
              className={`w-full rounded-lg px-3 py-2 text-sm text-left hover:bg-eh-cream disabled:opacity-40 disabled:hover:bg-transparent ${
                item.danger ? "text-rose-700" : "text-eh-ink"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
